import { useQuery } from '@tanstack/react-query';
import { useAccountParams } from '~/hooks/useAccountParams';
import { customFetch } from '~/utils/customFetch';
import { ErrorObjectString } from '~/utils/typescriptHelpers';
import useSessionPlQuery from '../useSessionPlQuery';

export type PartnerListObject = { id: number; label: string; extended?: number; category?: string };

type ResponseData = PartnerListObject[] | ErrorObjectString;

async function getPartners({
  accountId,
  query = null,
}: {
  accountId: number | string;
  query?: string | null;
}) {
  const { data } = await customFetch.get<ResponseData>(`/partnersJSON.pl`, {
    params: {
      client_id: String(accountId),
      ...(query ? { q: query } : {}),
    },
  });
  if ('error' in data) {
    throw new Error(data.error);
  }
  return data.map((partner) => ({
    label: partner.label,
    id: Number(partner.id),
    extended: partner.extended ? Number(partner.extended) : undefined,
    category: partner.category,
  }));
}

export function getPartnersQueryOptions(accountId?: number | string) {
  return {
    queryKey: ['partners', String(accountId)] as const,
    queryFn: async () => {
      if (accountId == null) {
        throw new Error('No account id');
      }
      return getPartners({ accountId });
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!accountId,
  };
}

const usePartnerData = () => {
  const accountParams = useAccountParams();
  const { data: sessionData } = useSessionPlQuery();
  const accountId = accountParams?.accountId ?? sessionData?.id;

  return useQuery(getPartnersQueryOptions(accountId));
};

export default usePartnerData;
